import React, { useEffect, useState } from 'react';
import CloseModalIcon from 'components/icons/CloseModalIcon';
import HorizontalLine from 'components/ui/HorizontalLine';
import { ProductData } from 'lib/bigcommerce/types';
import BCImage from 'components/BcImage';
import { numberToCurrency } from 'utils/currency';
import classnames from 'utils/classnames';
import MenuItemArrowLeftIcon from 'components/icons/MenuItemArrowLeftIcon';
import MenuItemArrowRightIcon from 'components/icons/MenuItemArrowRightIcon';
import CheckboxIcon from 'components/icons/CheckboxIcon';
import { SUBSCRIPTION_MODAL, SubscriptionActions } from 'lib/constants';
import { ModalProps } from './index';
import { SFSubscription } from 'pages/api/account/subscriptions';

type SubscriptionAddItemProps = {
  products: Array<ProductData>;
  subscription: SFSubscription;
  handleModal: (args: ModalProps) => void;
  handleAddItemsToSubscription: (addProducts: ProductData[]) => void;
};

const SubscriptionAddItemModal: React.FC<SubscriptionAddItemProps> = (props) => {
  const { products: allProducts, subscription, handleModal, handleAddItemsToSubscription } = props;
  const [products, setProducts] = useState<
    Array<
      ProductData & {
        selected?: boolean;
      }
    >
  >(allProducts);
  const [perPage] = useState(SUBSCRIPTION_MODAL.ADD_ITEM.ITEMS_PER_PAGE);
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const keyDownHandler = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        handleModal({ id: subscription.id, type: SubscriptionActions.ADD_ITEM, show: false });
      }
    };
    document.addEventListener('keydown', keyDownHandler);
    return () => {
      document.removeEventListener('keydown', keyDownHandler);
    };
  }, []);

  const handleOffset = (action: string) => {
    switch (action) {
      case 'previous':
        setOffset(offset - perPage >= 0 ? offset - perPage : offset);
        break;
      case 'next':
        setOffset(offset + perPage < allProducts.length ? offset + perPage : offset);
        break;
    }
  };

  const handleAddItem = () => {
    handleAddItemsToSubscription(products?.filter((product) => product?.selected));
    handleModal({ id: subscription.id, type: SubscriptionActions.ADD_ITEM, show: false });
  };

  return (
    <div className={'h-3/4 md:h-auto'}>
      <div
        className={
          'relative mx-auto h-full rounded-lg bg-white drop-shadow-sm md:min-h-[650px] md:w-[500px]'
        }
      >
        <div className={'flex h-full flex-col space-y-4 p-4'}>
          <div id={'addProductHeader'}>
            <div className="flex w-full flex-row items-center justify-between py-2">
              <p className="text-lg font-medium uppercase leading-none text-gray-800">
                Add products
              </p>
              <div
                onClick={() =>
                  handleModal({
                    id: null,
                    show: false,
                  })
                }
                className={'cursor-pointer'}
              >
                <CloseModalIcon className={'h-8 w-8'} />
              </div>
            </div>
            <HorizontalLine />
            <div className="flex w-full items-center justify-between space-x-4 py-2">
              <div className="flex h-full items-center justify-start space-x-2">
                <p className="text-sm leading-none text-gray-800">
                  {offset + 1} -{' '}
                  {offset + perPage < allProducts?.length ? offset + perPage : allProducts?.length}{' '}
                  of {allProducts?.length}
                </p>
              </div>
              <div className="flex items-start justify-start space-x-2">
                <div
                  className={
                    'flex h-[48px] w-[48px] cursor-pointer items-center justify-center rounded bg-sf-blue-700 bg-opacity-[4%]'
                  }
                  onClick={() => handleOffset('previous')}
                >
                  <span
                    className={`text-sf-blue-700 ${classnames({
                      'text-opacity-[48%]': offset === 0,
                    })}`}
                  >
                    <MenuItemArrowLeftIcon />
                  </span>
                </div>
                <div
                  className={
                    'flex h-[48px] w-[48px] cursor-pointer items-center justify-center rounded bg-sf-blue-700 bg-opacity-[4%]'
                  }
                  onClick={() => handleOffset('next')}
                >
                  <span
                    className={`text-sf-blue-700 ${classnames({
                      'text-opacity-[48%]': offset + perPage === allProducts?.length,
                    })}`}
                  >
                    <MenuItemArrowRightIcon />
                  </span>
                </div>
              </div>
            </div>
            <HorizontalLine />
          </div>
          <div id={'addProductContent'} className={'grow overflow-y-auto'}>
            <div className="flex flex-col items-start justify-center space-y-4">
              {products?.slice(offset, offset + perPage).map((product) => (
                <div
                  key={product.id}
                  className={`flex w-full cursor-pointer items-center justify-center space-x-4 rounded-lg px-2 py-1.5 ${classnames(
                    {
                      'bg-sf-blue-700 bg-opacity-[4%]': product?.selected,
                    }
                  )}`}
                  onClick={() =>
                    setProducts([
                      ...products.map((baseProduct) =>
                        baseProduct.id === product.id
                          ? {
                              ...baseProduct,
                              selected: !baseProduct.selected,
                            }
                          : baseProduct
                      ),
                    ])
                  }
                >
                  <div className="flex h-6 w-6 items-center justify-center rounded border-2 border-sf-blue-700 p-0.5">
                    {product?.selected && <CheckboxIcon className={'stroke-2'} />}
                  </div>
                  <div className="relative w-auto">
                    <div className="overflow-hidden rounded-md">
                      <div className="relative h-[64px] w-[64px]">
                        <BCImage
                          {...product.defaultImage}
                          className=""
                          layout="fixed"
                          objectFit="cover"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="inline-flex h-full flex-1 flex-col items-start justify-center">
                    <div className="flex w-full flex-col items-start justify-start space-y-2">
                      <div className="inline-flex w-full items-start justify-start">
                        <div className="w-3/4">
                          <p className="w-full text-base font-medium leading-tight text-gray-800">
                            {product.name}
                          </p>
                        </div>
                      </div>
                      <p className="text-sm leading-none text-gray-800">{`${numberToCurrency(
                        product.price,
                        product.currency
                      )}`}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div id={'addProductBottom'}>
            <div className="flex w-full flex-col space-y-6">
              <HorizontalLine />
              <div className="flex w-full items-center justify-end">
                <button
                  disabled={products?.filter((product) => product?.selected)?.length <= 0}
                  className={`flex h-full cursor-pointer items-center justify-center rounded-full border border-gray-800 px-4 py-3 ${classnames(
                    {
                      'opacity-30': products?.filter((product) => product?.selected)?.length <= 0,
                    }
                  )}`}
                  onClick={handleAddItem}
                >
                  <p className="text-center text-sm font-medium leading-none text-gray-800">
                    Add products ({products?.filter((product) => product?.selected)?.length || 0})
                  </p>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionAddItemModal;
